/**
 * ExpoQueueView — Expeditor view for order flow and driver dispatch.
 *
 * Shows orders ready for pickup, available drivers, and QC queue.
 * Follows DRR-055: Dumb Frontend — all data from backend.
 */

import { useMemo, useCallback, useState } from 'react';
import {
  Container,
  Title,
  Text,
  Group,
  Stack,
  Paper,
  SimpleGrid,
  Card,
  Badge,
  Button,
  ThemeIcon,
  Loader,
  Alert,
  Table,
  ActionIcon,
  Tooltip,
  Modal,
  Select,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconClipboardCheck,
  IconTruck,
  IconCheck,
  IconClock,
  IconAlertTriangle,
  IconRefresh,
  IconUser,
  IconMapPin,
} from '@tabler/icons-react';
import { useTenantId } from '@/auth';
import {
  useFoodServiceReadyForPickupQuery,
  useFoodServicePendingQcQuery,
  useFoodServiceAvailableDriversQuery,
  FoodServiceOrderStatus,
} from '@/generated/graphql';

export function ExpoQueueView() {
  const tenantId = useTenantId() ?? '';
  const skip = !tenantId;

  const [dispatchModalOpened, { open: openDispatchModal, close: closeDispatchModal }] = useDisclosure(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [selectedDriverId, setSelectedDriverId] = useState<string | null>(null);

  // Query orders ready for pickup using Food Service API
  const {
    data: readyData,
    loading: readyLoading,
    refetch: refetchReady,
  } = useFoodServiceReadyForPickupQuery({
    skip,
    pollInterval: 5000,
  });

  // Query orders pending QC using Food Service API
  const {
    data: qcData,
    loading: qcLoading,
    refetch: refetchQc,
  } = useFoodServicePendingQcQuery({
    skip,
    pollInterval: 5000,
  });

  // Query available drivers using Food Service API
  const {
    data: driversData,
    loading: driversLoading,
  } = useFoodServiceAvailableDriversQuery({
    skip,
    pollInterval: 10000,
  });

  const readyForPickup = readyData?.readyForPickup ?? [];
  const qcNeeded = qcData?.pendingQc ?? [];
  const availableDrivers = driversData?.availableDrivers ?? [];

  const handleRefresh = useCallback(() => {
    refetchReady();
    refetchQc();
  }, [refetchReady, refetchQc]);

  const handleDispatch = useCallback((orderId: string) => {
    setSelectedOrderId(orderId);
    openDispatchModal();
  }, [openDispatchModal]);

  const handleConfirmDispatch = useCallback(() => {
    if (selectedOrderId && selectedDriverId) {
      // TODO: Use assignDriver mutation
      console.log('Dispatching order:', selectedOrderId, 'to driver:', selectedDriverId);
      closeDispatchModal();
      setSelectedOrderId(null);
      setSelectedDriverId(null);
    }
  }, [selectedOrderId, selectedDriverId, closeDispatchModal]);

  const handleQCApprove = useCallback((orderId: string) => {
    // TODO: Use fireEvent mutation with qc_passed event
    console.log('QC approved:', orderId);
  }, []);

  if (readyLoading || qcLoading) {
    return (
      <Container size="xl" py="xl">
        <Group justify="center">
          <Loader size="lg" />
          <Text>Loading expo queue...</Text>
        </Group>
      </Container>
    );
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Group>
            <ThemeIcon size="xl" color="purple" variant="light">
              <IconClipboardCheck size={28} />
            </ThemeIcon>
            <div>
              <Title order={2}>Expo Queue</Title>
              <Text size="sm" c="dimmed">
                {readyForPickup.length} ready for pickup, {qcNeeded.length} need QC
              </Text>
            </div>
          </Group>
          <Tooltip label="Refresh queue">
            <ActionIcon variant="light" size="lg" onClick={handleRefresh}>
              <IconRefresh size={20} />
            </ActionIcon>
          </Tooltip>
        </Group>

        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
          {/* Ready for Pickup */}
          <Paper p="md" withBorder>
            <Group justify="space-between" mb="md">
              <Title order={4}>Ready for Pickup</Title>
              <Badge color="green" size="lg">
                {readyForPickup.length}
              </Badge>
            </Group>
            {readyForPickup.length === 0 ? (
              <Text c="dimmed" ta="center" py="xl">
                No orders ready for pickup
              </Text>
            ) : (
              <Stack gap="sm">
                {readyForPickup.map((order) => (
                  <Card key={order.id} withBorder padding="sm">
                    <Group justify="space-between">
                      <div>
                        <Text fw={500}>#{order.orderNumber} - {order.brand}</Text>
                        <Group gap="xs">
                          <IconMapPin size={12} />
                          <Text size="xs" c="dimmed" lineClamp={1}>
                            {order.deliveryAddress}
                          </Text>
                        </Group>
                        <Text size="xs" c="dimmed">
                          {order.items.length} items • {order.customerName}
                        </Text>
                      </div>
                      <Button
                        size="sm"
                        color="teal"
                        leftSection={<IconTruck size={16} />}
                        onClick={() => handleDispatch(order.id)}
                      >
                        Dispatch
                      </Button>
                    </Group>
                  </Card>
                ))}
              </Stack>
            )}
          </Paper>

          {/* QC Needed */}
          <Paper p="md" withBorder>
            <Group justify="space-between" mb="md">
              <Title order={4}>QC Needed</Title>
              <Badge color="yellow" size="lg">
                {qcNeeded.length}
              </Badge>
            </Group>
            {qcNeeded.length === 0 ? (
              <Text c="dimmed" ta="center" py="xl">
                No orders awaiting QC
              </Text>
            ) : (
              <Stack gap="sm">
                {qcNeeded.map((order) => (
                  <Card key={order.id} withBorder padding="sm">
                    <Group justify="space-between">
                      <div>
                        <Text fw={500}>#{order.orderNumber} - {order.brand}</Text>
                        <Text size="xs" c="dimmed">
                          {order.items.length} items • {order.customerName}
                        </Text>
                        <Group gap="xs" mt="xs">
                          {order.items.map((item) => (
                            <Badge key={item.id} size="xs" color="blue">
                              {item.quantity}x {item.name}
                            </Badge>
                          ))}
                        </Group>
                      </div>
                      <Button
                        size="sm"
                        color="green"
                        leftSection={<IconCheck size={16} />}
                        onClick={() => handleQCApprove(order.id)}
                      >
                        Approve
                      </Button>
                    </Group>
                  </Card>
                ))}
              </Stack>
            )}
          </Paper>
        </SimpleGrid>

        {/* Available Drivers */}
        <Paper p="md" withBorder>
          <Group justify="space-between" mb="md">
            <Title order={4}>Available Drivers</Title>
            <Badge color="teal" size="lg">
              {availableDrivers.length} online
            </Badge>
          </Group>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Driver</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Distance</Table.Th>
                <Table.Th>Action</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {availableDrivers.slice(0, 5).map((driver) => (
                <Table.Tr key={driver.id}>
                  <Table.Td>
                    <Group gap="xs">
                      <IconUser size={16} />
                      <Text size="sm">{driver.name}</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={driver.currentDeliveryId ? 'yellow' : 'green'} size="sm">
                      {driver.currentDeliveryId ? 'On Delivery' : 'Available'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {driver.distanceMiles?.toFixed(1) ?? '—'} mi
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Button
                      size="xs"
                      variant="light"
                      disabled={!!driver.currentDeliveryId}
                    >
                      Assign
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      </Stack>

      {/* Dispatch Modal */}
      <Modal
        opened={dispatchModalOpened}
        onClose={closeDispatchModal}
        title="Dispatch Order"
      >
        <Stack gap="md">
          <Text>Select a driver for Order #{selectedOrderId?.slice(-6)}</Text>
          <Select
            label="Driver"
            placeholder="Select driver"
            data={availableDrivers.map((d) => ({
              value: d.id,
              label: d.name,
            }))}
            value={selectedDriverId}
            onChange={setSelectedDriverId}
          />
          <Group justify="flex-end">
            <Button variant="light" onClick={closeDispatchModal}>
              Cancel
            </Button>
            <Button
              color="teal"
              onClick={handleConfirmDispatch}
              disabled={!selectedDriverId}
            >
              Dispatch
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

export default ExpoQueueView;
